import Slider, { SliderProps } from '../../../../slider';
import NextLink from 'next/link';
import Image from '../../../../../../frontastic/lib/image';
import imageUrlResize from '../../../../../../helpers/imageUrlResize';
import Typography from '../../../../../commercetools-ui/typography';
import { CurrencyHelpers } from '../../../../../../helpers/currencyHelpers';
import { OrderUtils } from '../../../../utils/OrderUtils';
import React from 'react';
import { useDateFormat, useFormat } from '../../../../../../helpers/hooks/useFormat';
import { ShippingHandler } from '../../../../checkout/utils/ShippingHandler';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, mobile, tablet } from 'helpers/utils/screensizes';
import { countryOptions } from 'helpers/countryOptions';
import LinkIcon from 'components/icons/link';

const OrderDetail = ({ order }) => {
  const { formatMessage: formatMessage } = useFormat({ name: 'product' });
  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { formatDate } = useDateFormat();

  const isBillingSameAsShipping = order && ShippingHandler.isEmptyAddress(order?.shippingAddress);
  const [isMobileSize] = useMediaQuery(mobile);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isDesktopSize] = useMediaQuery(desktop);

  const sliderConfiguration: SliderProps = {
    slidesPerView: isMobileSize ? (isTabletSize ? (isDesktopSize ? 3 : 2) : 1) : 1,
    arrows: isDesktopSize ? true : false,
    dots: true,
    spaceBetween: 16,
    autoHeight: true,
    navigationSize: 50,
    sliderVersion: 'product',
  };

  return (
    <>
      {order?.lineItems.length > 1 ? (
        <div className="relative">
          <Slider {...sliderConfiguration}>
            {order?.lineItems.slice(0, 15).map((product, index: number) => (
              <div
                className="PLP-product relative flex justify-center self-end transition-colors hover:bg-neutral-50"
                key={index}
              >
                <NextLink href={product._url || ''} className="group w-full">
                  <div className="relative aspect-[400/267] w-full overflow-hidden">
                    <Image
                      src={imageUrlResize(product.variant.images[0], 'large') || ''}
                      alt={product.name}
                      layout="fill"
                    />
                  </div>
                  <div className="p-4">
                    <div className="text-base font-bold text-neutral-950">{product.variant.attributes?.brand?.[0]}</div>
                    <h3
                      className="w-full overflow-hidden truncate text-base font-bold text-neutral-950"
                      title="{product.variants[0].attributes?.model_name?.[0]}"
                    >
                      {product.variant.attributes?.model_name?.[0]}
                    </h3>
                    <div className="text-sm">
                      {product.variant.attributes.model_year?.[0]}
                      <span className="px-2">&#183;</span>
                      {product.variant.attributes.frame_height_manufacturer?.[0]}
                      <span className="px-2">&#183;</span>
                      {product.variant.attributes.mileage_in_km?.[0]} km
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <div className="text-sm text-neutral-950 line-through" suppressHydrationWarning>
                        <Typography>
                          {formatMessage({
                            id: 'price.uvp',
                            defaultMessage: 'UVP {price}',
                            values: { price: CurrencyHelpers.formatForCurrency(product.variant.recommendedPrice) },
                          })}
                        </Typography>
                      </div>
                      <div
                        className="rounded-4xl bg-accent-300 px-2 py-0.5 text-sm font-semibold text-neutral-950"
                        suppressHydrationWarning
                      >
                        <Typography>{CurrencyHelpers.formatForCurrency(product.variant.price)}</Typography>
                      </div>
                    </div>
                  </div>
                </NextLink>
              </div>
            ))}
          </Slider>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3">
          <div className="relative flex justify-center self-end transition-colors hover:bg-neutral-50">
            <NextLink href={order?.lineItems[0]._url || ''} className="group w-full">
              <div className="relative aspect-[400/267] w-full overflow-hidden">
                <Image
                  src={imageUrlResize(order?.lineItems[0].variant.images[0], 'large') || ''}
                  alt={order?.lineItems[0].name}
                  layout="fill"
                />
              </div>
              <div className="p-4">
                <div className="text-base font-bold text-neutral-950">
                  {order?.lineItems[0].variant.attributes?.brand?.[0]}
                </div>
                <h3 className="w-full overflow-hidden truncate text-base font-bold text-neutral-950">
                  {order?.lineItems[0].variant.attributes?.model_name?.[0]}
                </h3>
                <div className="text-sm">
                  {order?.lineItems[0].variant.attributes.model_year?.[0]}
                  <span className="px-2">&#183;</span>
                  {order?.lineItems[0].variant.attributes.frame_height_manufacturer?.[0]}
                  <span className="px-2">&#183;</span>
                  {order?.lineItems[0].variant.attributes.mileage_in_km?.[0]} km
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <div className="text-sm text-neutral-950 line-through" suppressHydrationWarning>
                    <Typography>
                      {formatMessage({
                        id: 'price.uvp',
                        defaultMessage: 'UVP {price}',
                        values: {
                          price: CurrencyHelpers.formatForCurrency(order?.lineItems[0].variant.recommendedPrice),
                        },
                      })}
                    </Typography>
                  </div>
                  <div
                    className="rounded-4xl bg-accent-300 px-2 py-0.5 text-sm font-semibold text-neutral-950"
                    suppressHydrationWarning
                  >
                    <Typography>{CurrencyHelpers.formatForCurrency(order?.lineItems[0].variant.price)}</Typography>
                  </div>
                </div>
              </div>
            </NextLink>
          </div>
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="bg-neutral-50 p-4">
          <div className="mb-4 font-headline text-lg">
            {formatAccountMessage({ id: 'order.information', defaultMessage: 'Bestellinformationen' })}
          </div>
          <div className="flex flex-col gap-2">
            <span
              dangerouslySetInnerHTML={{
                __html: formatAccountMessage({
                  id: 'orders.date',
                  values: { date: formatDate(order.createdAt) },
                }),
              }}
            ></span>
            <span>
              {formatAccountMessage({
                id: `order.status`,
              })}
              :{' '}
              <strong>
                {formatAccountMessage({
                  id: `order.status.${OrderUtils.getOrderState(order)}`,
                  defaultMessage: OrderUtils.getOrderState(order),
                })}
              </strong>
            </span>
            <span
              dangerouslySetInnerHTML={{
                __html: formatAccountMessage({
                  id: 'order.number',
                  values: { orderNumber: order.orderId },
                }),
              }}
            ></span>
          </div>
        </div>
        <div className="bg-neutral-50 p-4">
          <div className="mb-4 font-headline text-lg">
            {formatAccountMessage({ id: 'delivery.information', defaultMessage: 'Lieferinformationen' })}
          </div>
          <div>
            <span className="mb-2 block">{formatCheckoutMessage({ id: 'shippingAddress' })}:</span>
            {isBillingSameAsShipping ? (
              <div className="flex flex-col font-semibold">
                <span>
                  {order.billingAddress.streetName} {order.billingAddress.streetNumber}
                </span>
                <span>
                  {order.billingAddress.postalCode} {order.billingAddress.city}
                </span>
                <span>{countryOptions.find((option) => option.data === order.billingAddress.country)?.display}</span>
              </div>
            ) : (
              <div className="flex flex-col font-semibold">
                <span>
                  {order.shippingAddress.streetName} {order.shippingAddress.streetNumber}
                </span>
                <span>
                  {order.shippingAddress.postalCode} {order.shippingAddress.city}
                </span>
                <span>{countryOptions.find((option) => option.data === order.shippingAddress.country)?.display}</span>
              </div>
            )}
          </div>
        </div>
        <div className="bg-neutral-50 p-4">
          <div className="mb-4 font-headline text-lg">
            {formatAccountMessage({ id: 'payment.information', defaultMessage: 'Zahlungsinformationen' })}
          </div>
          <div>
            <span>
              {formatAccountMessage({ id: 'payment.method', defaultMessage: 'Zahlungsmethode: ' })}
              <strong>{order.payments[0].paymentMethod}</strong>
            </span>
          </div>
          {isBillingSameAsShipping ? (
            <div className="mt-4 flex items-center gap-2">
              <input
                type="checkbox"
                className="size-6 rounded border-2 checked:border-neutral-400 checked:bg-neutral-400"
                checked
                disabled
              />
              <span className="text-sm">
                {formatAccountMessage({
                  id: 'billingAddressSameAsShipping',
                  defaultMessage: 'Die Rechnungsadresse entspricht der Lieferadresse',
                })}
              </span>
            </div>
          ) : (
            <div>
              <span className="mb-2 block">{formatCheckoutMessage({ id: 'billingAddress' })}:</span>
              <div className="flex flex-col font-semibold">
                <span>
                  {order.billingAddress.streetName} {order.billingAddress.streetNumber}
                </span>
                <span>
                  {order.billingAddress.postalCode} {order.billingAddress.city}
                </span>
                <span>{order.billingAddress.country}</span>
              </div>
            </div>
          )}
        </div>
        {order.insurance?.hasInsurance && (
          <div className="bg-neutral-50 p-4">
            <div className="mb-4 font-headline text-lg">
              {formatAccountMessage({ id: 'insurance.information', defaultMessage: 'Versicherungsinformationen' })}
            </div>
            <div className="flex gap-2">
              Versicherung:
              <a
                href={formatAccountMessage({
                  id: 'insurance.information.link',
                  defaultMessage: 'https://basecamp.bikmo.com/?ref=LoopFamily',
                })}
                className="flex gap-2 text-secondary-600"
                target="_blank"
              >
                Bikmo
                <LinkIcon className="size-6 fill-secondary-600" />
              </a>
            </div>
          </div>
        )}

        {/*<div className="bg-neutral-50 p-4">*/}
        {/*  <div className="mb-4 font-headline text-lg">*/}
        {/*    {formatAccountMessage({ id: 'refund.information', defaultMessage: 'Gutschriftsinformation' })}*/}
        {/*  </div>*/}
        {/*  <div>*/}
        {/*    <span*/}
        {/*      dangerouslySetInnerHTML={{*/}
        {/*        __html: formatAccountMessage({*/}
        {/*          id: 'refund.information.text',*/}
        {/*          values: { amount: '1.299€', date: '14.04.2025' },*/}
        {/*        }),*/}
        {/*      }}*/}
        {/*    ></span>*/}
        {/*  </div>*/}
        {/*</div>*/}
      </div>
    </>
  );
};

export default OrderDetail;
